import type { ClientFormValues } from '../models/Forms';

import {
  getIdentityDocumentValidationFingerprint,
  shouldRunIdentityDocumentValidation,
} from './identityDocumentFingerprint';

export type IdentityDocumentRegistryFeedbackTone = 'success' | 'error' | 'warning';

export type IdentityDocumentRegistryFeedback = {
  tone: IdentityDocumentRegistryFeedbackTone;
  messageKey:
    | 'clientForm.identityDocumentValidation.clearSuccess'
    | 'clientForm.identityDocumentValidation.listedRegistryNotice'
    | 'clientForm.identityDocumentValidation.registryInvalidDocumentNumber'
    | 'clientForm.identityDocumentValidation.verificationFailedMessage';
};

export function resolveIdentityDocumentRegistryFeedback(
  values: ClientFormValues,
): IdentityDocumentRegistryFeedback | null {
  if (!shouldRunIdentityDocumentValidation(values)) {
    return null;
  }

  const fingerprint = getIdentityDocumentValidationFingerprint(values);
  if (values.identityDocumentValidationFingerprint !== fingerprint) {
    return null;
  }

  const status = values.identityDocumentValidationStatus;
  if (status === 'validated') {
    return {
      tone: 'success',
      messageKey: 'clientForm.identityDocumentValidation.clearSuccess',
    };
  }
  if (status === 'listed') {
    return {
      tone: 'error',
      messageKey: 'clientForm.identityDocumentValidation.listedRegistryNotice',
    };
  }
  if (status === 'invalid') {
    return {
      tone: 'warning',
      messageKey: 'clientForm.identityDocumentValidation.registryInvalidDocumentNumber',
    };
  }
  if (status === 'verificationFailed') {
    return {
      tone: 'warning',
      messageKey: 'clientForm.identityDocumentValidation.verificationFailedMessage',
    };
  }

  return null;
}

function withIdentityDocumentRegistryMeta(
  values: ClientFormValues,
  status: NonNullable<ClientFormValues['identityDocumentValidationStatus']>,
): ClientFormValues {
  return {
    ...values,
    identityDocumentValidationStatus: status,
    identityDocumentValidationFingerprint: getIdentityDocumentValidationFingerprint(values),
  };
}

export function withListedIdentityDocumentValidationMeta(values: ClientFormValues): ClientFormValues {
  return withIdentityDocumentRegistryMeta(values, 'listed');
}

export function withInvalidIdentityDocumentRegistryMeta(values: ClientFormValues): ClientFormValues {
  return withIdentityDocumentRegistryMeta(values, 'invalid');
}

export function withVerificationFailedIdentityDocumentMeta(values: ClientFormValues): ClientFormValues {
  return withIdentityDocumentRegistryMeta(values, 'verificationFailed');
}

export function withValidatedIdentityDocumentMeta(values: ClientFormValues): ClientFormValues {
  const fingerprint = getIdentityDocumentValidationFingerprint(values);
  if (
    values.identityDocumentValidationStatus === 'validated' &&
    values.identityDocumentValidationFingerprint === fingerprint
  ) {
    return values;
  }
  return withIdentityDocumentRegistryMeta(values, 'validated');
}
